import { Component } from '@theme/component';

/** Thumbnails shown at once on desktop; the "+ N" tile stands for the rest. */
const THUMBS_SHOWN = 4;
/** Average luminance (0–1) under which white reads better than ink on the photo. */
const DARK_PHOTO = 0.5;
/** Height of the area the floating phone header covers, for its contrast check. */
const HEADER_AREA = 60;

/**
 * dm-gallery-standard — the product gallery on both product templates.
 *
 * Two behaviours over one markup tree (snippets/dm-gallery-standard.liquid):
 *   · up to 1023px the stage is a native scroll-snap carousel. Swipe, momentum and
 *     reduced-motion behaviour come from the platform; this class only mirrors the scroll
 *     position into the counter and the progress line.
 *   · from 1024px only the active slide is opaque, driven by the thumbs, arrows and "+ N"
 *     tile, which is the 200ms crossfade the spec asks for (206:6931).
 *
 * It also listens for `dm:variant-change` so picking a colour moves the stage to that
 * variant's featured photo, and reads the active photo's brightness so the counter (and on
 * phones the floating header) stays legible over a dark photo. If the photo cannot be read,
 * they simply keep their dark default.
 *
 * First paint: Liquid marks the selected variant's slide active (a ?variant= link lands on
 * its photo). The index starts there, and in carousel mode the stage is scrolled to it.
 */
class DmGalleryStandard extends Component {
  requiredRefs = ['stage'];

  #index = 0;

  /** @type {Record<string, number>} */
  #variantMedia = {};

  /** @type {MediaQueryList | null} */
  #carousel = null;

  /** @type {MediaQueryList | null} */
  #phone = null;

  connectedCallback() {
    super.connectedCallback();

    this.#readVariantMedia();
    this.#carousel = window.matchMedia?.('(max-width: 1023px)') || null;
    this.#phone = window.matchMedia?.('(max-width: 749px)') || null;

    const initial = this.querySelector('[data-dm-slide][data-active="true"]');
    this.#index = initial instanceof HTMLElement ? Number(initial.dataset.index) || 0 : 0;

    this.addEventListener('click', this.#onClick);
    this.refs.stage?.addEventListener('scroll', this.#onScroll, { passive: true });
    document.addEventListener('dm:variant-change', this.#onVariantChange);

    this.#render();
    this.#scrollToIndex('auto');
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener('click', this.#onClick);
    this.refs.stage?.removeEventListener('scroll', this.#onScroll);
    document.removeEventListener('dm:variant-change', this.#onVariantChange);
    window.clearTimeout(this.#scrollTimer);
    document.querySelector('#header-component')?.removeAttribute('data-dm-on-dark');
  }

  #onClick = (/** @type {Event} */ event) => {
    const target = /** @type {HTMLElement} */ (event.target);

    const thumb = target.closest('[data-dm-thumb]');
    if (thumb instanceof HTMLElement) {
      this.#go(Number(thumb.dataset.index) || 0, true);
      return;
    }

    const more = target.closest('[data-dm-more]');
    if (more instanceof HTMLElement) {
      this.#go(Number(more.dataset.index) || 0, true);
      return;
    }

    const step = target.closest('[data-dm-step]');
    if (step instanceof HTMLElement) {
      const slides = this.#slides();
      if (!slides.length) return;
      const next = this.#position() + (Number(step.dataset.dmStep) || 0);
      const slide = slides[(next + slides.length) % slides.length];
      this.#go(Number(slide?.dataset.index) || 0, true);
    }
  };

  #scrollTimer = 0;

  /**
   * Scroll position is only authoritative in carousel mode, where the stage actually
   * scrolls. Debounced so a flick does not write the counter on every frame.
   */
  #onScroll = () => {
    if (!this.#carousel?.matches) return;
    window.clearTimeout(this.#scrollTimer);
    this.#scrollTimer = window.setTimeout(() => {
      const stage = this.refs.stage;
      if (!(stage instanceof HTMLElement) || stage.clientWidth === 0) return;
      const position = Math.round(stage.scrollLeft / stage.clientWidth);
      const slide = this.#slides()[position];
      const index = slide ? Number(slide.dataset.index) || 0 : position;
      if (index === this.#index) return;
      this.#index = index;
      this.#render();
    }, 80);
  };

  #onVariantChange = (/** @type {Event} */ event) => {
    const detail = /** @type {CustomEvent} */ (event).detail;
    const id = detail?.variantId;
    if (id == null) return;

    const index = this.#variantMedia[String(id)];
    // A variant with no featured media leaves the stage where the customer put it,
    // rather than snapping back to the first photo for no reason.
    if (typeof index === 'number') this.#go(index, true);
  };

  /**
   * @param {number} index
   * @param {boolean} [scroll]
   */
  #go(index, scroll) {
    const slides = this.#slides();
    if (!slides.length) return;

    // Slide indices follow the media positions (data-index), so clamp to the real range.
    const indices = slides.map((slide) => Number(slide.dataset.index) || 0);
    this.#index = Math.max(Math.min(...indices), Math.min(index, Math.max(...indices)));
    this.#render();

    if (!scroll) return;
    const reduced = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
    this.#scrollToIndex(reduced ? 'auto' : 'smooth');
  }

  /**
   * In carousel mode the stage is a scroller: bring the active slide into view.
   * @param {ScrollBehavior} behavior
   */
  #scrollToIndex(behavior) {
    const stage = this.refs.stage;
    if (!(stage instanceof HTMLElement) || !this.#carousel?.matches) return;
    const position = this.#position();
    if (position < 0) return;
    const left = position * stage.clientWidth;
    if (Math.abs(stage.scrollLeft - left) > 1) stage.scrollTo({ left, behavior });
  }

  /** The active slide's place among the rendered slides (media without a photo has none). */
  #position() {
    return this.#slides().findIndex((slide) => Number(slide.dataset.index) === this.#index);
  }

  #render() {
    const slides = this.#slides();
    for (const slide of slides) {
      slide.dataset.active = String(Number(slide.dataset.index) === this.#index);
    }

    const position = Math.max(0, this.#position());
    const total = slides.length;

    const count = this.refs.count;
    if (count instanceof HTMLElement) count.textContent = `${position + 1} / ${total}`;

    const progress = this.refs.progress;
    if (progress instanceof HTMLElement) progress.style.setProperty('--dm-gallery-position', String(position));

    // Four thumbs at a time, keeping one before the active photo in view when there is one.
    const start = Math.max(0, Math.min(position - 1, total - THUMBS_SHOWN));
    const thumbs = /** @type {HTMLElement[]} */ (Array.from(this.querySelectorAll('[data-dm-thumb]')));
    thumbs.forEach((thumb, place) => {
      thumb.setAttribute('aria-pressed', String(Number(thumb.dataset.index) === this.#index));
      thumb.hidden = place < start || place >= start + THUMBS_SHOWN;
    });

    const more = this.refs.more;
    if (more instanceof HTMLElement) {
      const remaining = total - (start + THUMBS_SHOWN);
      more.hidden = remaining <= 0;
      if (remaining > 0) {
        more.textContent = `+ ${remaining}`;
        more.setAttribute('aria-label', `Show ${remaining} more photos`);
        more.dataset.index = slides[start + THUMBS_SHOWN]?.dataset.index ?? '';
      }
    }

    this.#applyTone();
  }

  /** Flip the counter, and the floating phone header, to white over a dark photo. */
  #applyTone() {
    const frame = this.refs.frame;
    const image = this.querySelector('[data-dm-slide][data-active="true"] img');
    if (!(frame instanceof HTMLElement) || !(image instanceof HTMLImageElement)) return;

    if (!image.complete || !image.naturalWidth) {
      image.addEventListener('load', () => this.#applyTone(), { once: true });
      return;
    }

    const box = frame.getBoundingClientRect();
    const count = this.refs.count;
    if (count instanceof HTMLElement) {
      const rect = count.getBoundingClientRect();
      const level = this.#luminance(image, box, {
        x: rect.left - box.left,
        y: rect.top - box.top,
        width: rect.width,
        height: rect.height,
      });
      if (level !== null) frame.dataset.counterTone = level < DARK_PHOTO ? 'dark' : 'light';
    }

    if (this.#phone?.matches) {
      const level = this.#luminance(image, box, { x: 0, y: 0, width: box.width, height: HEADER_AREA });
      if (level !== null) {
        document.querySelector('#header-component')?.toggleAttribute('data-dm-on-dark', level < DARK_PHOTO);
      }
    }
  }

  /**
   * Average luminance of a frame area, following the photo's object-fit: cover crop.
   * Null when it cannot be read (a cross-origin photo taints the canvas).
   * @param {HTMLImageElement} image
   * @param {DOMRect} frame
   * @param {{ x: number, y: number, width: number, height: number }} area
   * @returns {number | null}
   */
  #luminance(image, frame, area) {
    if (!frame.width || !frame.height || !area.width || !area.height) return null;

    const scale = Math.max(frame.width / image.naturalWidth, frame.height / image.naturalHeight);
    const offsetX = (image.naturalWidth - frame.width / scale) / 2;
    const offsetY = (image.naturalHeight - frame.height / scale) / 2;

    const canvas = document.createElement('canvas');
    canvas.width = 8;
    canvas.height = 8;
    const context = canvas.getContext('2d', { willReadFrequently: true });
    if (!context) return null;

    try {
      context.drawImage(
        image,
        offsetX + area.x / scale,
        offsetY + area.y / scale,
        area.width / scale,
        area.height / scale,
        0,
        0,
        8,
        8
      );
      const { data } = context.getImageData(0, 0, 8, 8);
      let sum = 0;
      for (let i = 0; i < data.length; i += 4) {
        sum += 0.2126 * (data[i] ?? 0) + 0.7152 * (data[i + 1] ?? 0) + 0.0722 * (data[i + 2] ?? 0);
      }
      return sum / (data.length / 4) / 255;
    } catch {
      return null;
    }
  }

  /** @returns {HTMLElement[]} */
  #slides() {
    return Array.from(this.querySelectorAll('[data-dm-slide]'));
  }

  #readVariantMedia() {
    const element = this.querySelector('[data-dm-variant-media]');
    if (!element?.textContent) return;
    try {
      this.#variantMedia = JSON.parse(element.textContent);
    } catch {
      // Variant photo swapping is an enhancement — the gallery still works without it.
    }
  }
}

if (!customElements.get('dm-gallery-standard')) {
  customElements.define('dm-gallery-standard', DmGalleryStandard);
}
